(() => {
  'use strict';

  if (window.Liva7Runtime) return;

  const mobile = window.matchMedia('(max-width: 749px)');
  const coarsePointer = window.matchMedia('(hover: none) and (pointer: coarse)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const observerPools = new Map();
  const scrollSubscribers = new Set();
  let frameId = 0;
  let scrollListening = false;

  const saveDataEnabled = () => Boolean(navigator.connection?.saveData);
  const isLowPower = () => mobile.matches || coarsePointer.matches || reducedMotion.matches || saveDataEnabled();
  const toElements = (value) => {
    if (!value) return [];
    if (value instanceof Element) return [value];
    return Array.from(value).filter((item) => item instanceof Element);
  };

  const queueScrollFrame = () => {
    if (frameId) return;
    frameId = requestAnimationFrame(() => {
      frameId = 0;
      const scrollY = window.scrollY || window.pageYOffset;
      const viewportHeight = window.innerHeight;

      scrollSubscribers.forEach((subscriber) => {
        const delta = scrollY - subscriber.lastScrollY;
        subscriber.lastScrollY = scrollY;
        if (!subscriber.allowLowPower && isLowPower()) return;
        if (!subscriber.owner.isConnected) return;

        const rect = subscriber.owner.getBoundingClientRect();
        if (rect.bottom < -subscriber.margin || rect.top > viewportHeight + subscriber.margin) return;
        subscriber.callback({ delta, rect, scrollY, viewportHeight });
      });
    });
  };

  const startScrollDispatcher = () => {
    if (scrollListening) return;
    scrollListening = true;
    window.addEventListener('scroll', queueScrollFrame, { passive: true });
    window.addEventListener('resize', queueScrollFrame, { passive: true });
  };

  const stopScrollDispatcher = () => {
    if (!scrollListening || scrollSubscribers.size) return;
    scrollListening = false;
    window.removeEventListener('scroll', queueScrollFrame);
    window.removeEventListener('resize', queueScrollFrame);
    if (frameId) cancelAnimationFrame(frameId);
    frameId = 0;
  };

  const subscribeToScrollFrame = (owner, callback, options = {}) => {
    if (options.allowLowPower !== true && isLowPower()) return () => {};
    const subscriber = {
      owner,
      callback,
      allowLowPower: options.allowLowPower === true,
      margin: Number.isFinite(options.margin) ? options.margin : 160,
      lastScrollY: window.scrollY || window.pageYOffset,
    };
    scrollSubscribers.add(subscriber);
    startScrollDispatcher();
    queueScrollFrame();
    return () => {
      scrollSubscribers.delete(subscriber);
      stopScrollDispatcher();
    };
  };

  const observerKey = (options, once) => JSON.stringify({
    rootMargin: options.rootMargin || '0px',
    threshold: options.threshold ?? 0,
    once,
  });

  const observerPool = (options, once) => {
    const key = observerKey(options, once);
    if (observerPools.has(key)) return observerPools.get(key);

    const callbacks = new Map();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const handlers = callbacks.get(entry.target);
        if (!handlers) return;
        handlers.forEach((handler) => handler(entry));
        if (once && entry.isIntersecting) {
          callbacks.delete(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: options.rootMargin, threshold: options.threshold });

    const pool = { callbacks, observer };
    observerPools.set(key, pool);
    return pool;
  };

  const observe = (elements, callback, options = {}, once = false) => {
    const targets = toElements(elements);
    if (!targets.length) return () => {};

    if (!('IntersectionObserver' in window) || (options.motionOnly && isLowPower())) {
      targets.forEach((target) => callback({ target, isIntersecting: true, intersectionRatio: 1 }));
      return () => {};
    }

    const pool = observerPool(options, once);
    targets.forEach((target) => {
      if (!pool.callbacks.has(target)) pool.callbacks.set(target, new Set());
      pool.callbacks.get(target).add(callback);
      pool.observer.observe(target);
    });

    return () => {
      targets.forEach((target) => {
        const handlers = pool.callbacks.get(target);
        if (!handlers) return;
        handlers.delete(callback);
        if (!handlers.size) {
          pool.callbacks.delete(target);
          pool.observer.unobserve(target);
        }
      });
    };
  };

  const createScope = (owner) => {
    const cleanups = new Set();
    let destroyed = false;

    const track = (cleanup) => {
      if (typeof cleanup !== 'function') return cleanup;
      if (destroyed) cleanup();
      else cleanups.add(cleanup);
      return cleanup;
    };

    return {
      get destroyed() { return destroyed; },
      listen(target, type, handler, options) {
        if (!target?.addEventListener) return () => {};
        target.addEventListener(type, handler, options);
        return track(() => target.removeEventListener(type, handler, options));
      },
      observe(elements, callback, options = {}) {
        return track(observe(elements, callback, options, false));
      },
      observeOnce(elements, callback, options = {}) {
        return track(observe(elements, callback, options, true));
      },
      onScrollFrame(callback, options = {}) {
        return track(subscribeToScrollFrame(owner, callback, options));
      },
      timeout(callback, delay) {
        const timeoutId = window.setTimeout(() => {
          cleanups.delete(cancel);
          if (!destroyed) callback();
        }, delay);
        const cancel = () => {
          window.clearTimeout(timeoutId);
          cleanups.delete(cancel);
        };
        return track(cancel);
      },
      interval(callback, delay) {
        const intervalId = window.setInterval(() => {
          if (!destroyed) callback();
        }, delay);
        const cancel = () => {
          window.clearInterval(intervalId);
          cleanups.delete(cancel);
        };
        return track(cancel);
      },
      animate(duration, callback, options = {}) {
        if (isLowPower() && options.allowLowPower !== true) {
          callback(1);
          return () => {};
        }
        const start = performance.now();
        let animationId = 0;
        const cancel = () => {
          if (animationId) cancelAnimationFrame(animationId);
          animationId = 0;
        };
        const step = (time) => {
          if (destroyed) return;
          const progress = Math.min((time - start) / duration, 1);
          callback(progress);
          if (progress < 1) animationId = requestAnimationFrame(step);
          else cleanups.delete(cancel);
        };
        animationId = requestAnimationFrame(step);
        return track(cancel);
      },
      track,
      destroy() {
        if (destroyed) return;
        destroyed = true;
        cleanups.forEach((cleanup) => cleanup());
        cleanups.clear();
      },
    };
  };

  const register = (tagName, constructor) => {
    if (!customElements.get(tagName)) customElements.define(tagName, constructor);
  };

  [mobile, coarsePointer, reducedMotion].forEach((query) => {
    if (query.addEventListener) query.addEventListener('change', queueScrollFrame);
    else query.addListener(queueScrollFrame);
  });
  document.addEventListener('visibilitychange', queueScrollFrame);

  window.Liva7Runtime = Object.freeze({
    createScope,
    isLowPower,
    mobile,
    register,
    reducedMotion,
    version: '1.0.0',
  });
})();
